import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PatternComparison } from '@/services/PatternComparisonService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter } from 'recharts';

interface PatternChangeChartProps {
  patterns: PatternComparison[];
}

export const PatternChangeChart: React.FC<PatternChangeChartProps> = ({ patterns }) => {
  // Prepare data for status distribution
  const statusData = React.useMemo(() => {
    const counts = {
      created: 0,
      updated: 0,
      deleted: 0,
      unchanged: 0
    };

    patterns.forEach(pattern => {
      if (pattern.status in counts) {
        counts[pattern.status as keyof typeof counts]++;
      }
    });

    return [
      { name: 'Created', value: counts.created, color: '#10B981' },
      { name: 'Updated', value: counts.updated, color: '#3B82F6' },
      { name: 'Deleted', value: counts.deleted, color: '#EF4444' },
      { name: 'Unchanged', value: counts.unchanged, color: '#6B7280' }
    ];
  }, [patterns]);

  // Prepare data for frequency change distribution
  const frequencyChangeData = React.useMemo(() => {
    const buckets = {
      'Large Decrease (-50+)': 0,
      'Moderate Decrease (-10 to -50)': 0,
      'Small Decrease (-1 to -10)': 0,
      'No Change (0)': 0,
      'Small Increase (+1 to +10)': 0,
      'Moderate Increase (+10 to +50)': 0,
      'Large Increase (+50+)': 0
    };

    patterns.forEach(pattern => {
      const change = pattern.changes?.frequency ? 
        (pattern.changes.frequency.after - pattern.changes.frequency.before) : 0;
      
      if (change <= -50) buckets['Large Decrease (-50+)']++;
      else if (change <= -10) buckets['Moderate Decrease (-10 to -50)']++;
      else if (change < 0) buckets['Small Decrease (-1 to -10)']++;
      else if (change === 0) buckets['No Change (0)']++;
      else if (change <= 10) buckets['Small Increase (+1 to +10)']++;
      else if (change <= 50) buckets['Moderate Increase (+10 to +50)']++;
      else buckets['Large Increase (+50+)']++;
    });

    return Object.entries(buckets).map(([name, count]) => ({
      name,
      count
    }));
  }, [patterns]);

  // Prepare scatter plot data for frequency vs success rate changes
  const scatterData = React.useMemo(() => {
    return patterns
      .filter(p => p.changes?.frequency && p.changes?.success_rate)
      .map(pattern => ({
        frequencyChange: pattern.changes!.frequency!.after - pattern.changes!.frequency!.before,
        successRateChange: pattern.changes!.success_rate!.after - pattern.changes!.success_rate!.before,
        status: pattern.status
      }))
      .slice(0, 100); // Limit to first 100 for performance
  }, [patterns]);

  // Top patterns with biggest improvements
  const topImproved = React.useMemo(() => {
    return patterns
      .filter(p => p.changes?.success_rate && 
        (p.changes.success_rate.after - p.changes.success_rate.before) > 0)
      .sort((a, b) => {
        const aChange = a.changes!.success_rate!.after - a.changes!.success_rate!.before;
        const bChange = b.changes!.success_rate!.after - b.changes!.success_rate!.before;
        return bChange - aChange;
      })
      .slice(0, 5)
      .map(pattern => ({
        pattern: pattern.pattern || 'N/A',
        improvement: (pattern.changes!.success_rate!.after - pattern.changes!.success_rate!.before).toFixed(2)
      }));
  }, [patterns]);

  // Top patterns with biggest declines
  const topDeclined = React.useMemo(() => {
    return patterns
      .filter(p => p.changes?.success_rate && 
        (p.changes.success_rate.after - p.changes.success_rate.before) < 0)
      .sort((a, b) => {
        const aChange = a.changes!.success_rate!.after - a.changes!.success_rate!.before;
        const bChange = b.changes!.success_rate!.after - b.changes!.success_rate!.before;
        return aChange - bChange;
      })
      .slice(0, 5)
      .map(pattern => ({
        pattern: pattern.pattern || 'N/A',
        decline: (pattern.changes!.success_rate!.after - pattern.changes!.success_rate!.before).toFixed(2)
      }));
  }, [patterns]);

  if (patterns.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pattern Change Analytics</CardTitle>
          <CardDescription>No data available for analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No patterns to analyze
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Change Status Distribution</CardTitle>
          <CardDescription>Overview of pattern change types</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                labelFormatter={(label) => `Status: ${label}`}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {statusData.map((entry, index) => (
              <div key={index} className="flex items-center gap-1 text-sm">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                />
                <span>{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Frequency Change Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Frequency Change Distribution</CardTitle>
          <CardDescription>How pattern frequencies changed</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={frequencyChangeData} margin={{ left: 0, right: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                fontSize={10}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Frequency vs Success Rate Scatter Plot */}
      <Card>
        <CardHeader>
          <CardTitle>Frequency vs Success Rate Changes</CardTitle>
          <CardDescription>Correlation between frequency and performance changes</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <ScatterChart data={scatterData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="frequencyChange" 
                name="Frequency Change"
                fontSize={12}
              />
              <YAxis 
                dataKey="successRateChange" 
                name="Success Rate Change (%)"
                fontSize={12}
              />
              <Tooltip 
                formatter={(value: number, name: string) => [
                  `${value.toFixed(2)}${name.includes('Success') ? '%' : ''}`,
                  name.replace('Change', ' Change')
                ]}
              />
              <Scatter 
                name="Patterns" 
                dataKey="successRateChange"
                fill="hsl(var(--primary))"
                fillOpacity={0.6}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Changes</CardTitle>
          <CardDescription>Patterns with significant success rate changes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {topImproved.length > 0 && (
            <div>
              <h4 className="font-medium text-green-600 mb-2">Top Improved Patterns</h4>
              <div className="space-y-1">
                {topImproved.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="font-mono text-xs text-muted-foreground truncate max-w-[200px]">
                      {item.pattern}
                    </span>
                    <span className="text-green-600 font-medium">+{item.improvement}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topDeclined.length > 0 && (
            <div>
              <h4 className="font-medium text-red-600 mb-2">Most Declined Patterns</h4>
              <div className="space-y-1">
                {topDeclined.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="font-mono text-xs text-muted-foreground truncate max-w-[200px]">
                      {item.pattern}
                    </span>
                    <span className="text-red-600 font-medium">{item.decline}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topImproved.length === 0 && topDeclined.length === 0 && (
            <div className="text-center text-muted-foreground py-4">
              No significant performance changes detected
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};